import React, { useEffect, useState } from 'react';
import { adaptiveLearningService, ImprovedTopic, SubjectStat } from '../services/adaptiveLearningService';

const InsightsPanel: React.FC = () => {
    const [improvedTopics, setImprovedTopics] = useState<ImprovedTopic[]>([]);
    const [subjectStats, setSubjectStats] = useState<SubjectStat[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInsights = async () => {
            setLoading(true);
            const data = await adaptiveLearningService.getInsights();
            setImprovedTopics(data.improvedTopics);
            setSubjectStats(data.subjectStats);
            setLoading(false);
        };

        fetchInsights();
    }, []);

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
                <div className="flex items-center justify-center h-32 text-slate-400">
                    <i className="fas fa-spinner fa-spin text-2xl mr-3"></i> Loading insights...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Most Improved Topics */}
            {improvedTopics.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-3xl shadow-lg border border-green-200">
                    <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center">
                        <i className="fas fa-arrow-trend-up text-green-600 mr-2"></i>
                        Most Improved Topics
                    </h3>
                    <div className="space-y-3">
                        {improvedTopics.map((topic, idx) => (
                            <div
                                key={idx}
                                className="flex items-center justify-between p-4 bg-white rounded-xl border border-green-200"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-slate-800">{topic.topic}</span>
                                        <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full font-semibold">
                                            {topic.subject}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="text-slate-500">
                                            Started: {Math.round(topic.early_avg)}%
                                        </span>
                                        <i className="fas fa-arrow-right text-green-500"></i>
                                        <span className="text-green-700 font-semibold">
                                            Now: {Math.round(topic.recent_avg)}%
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-green-600">
                                        +{Math.round(topic.improvement)}%
                                    </div>
                                    <div className="text-xs text-green-700">improvement</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Subject Statistics */}
            {subjectStats.length > 0 && (
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                        <i className="fas fa-chart-bar text-indigo-600 mr-2"></i>
                        Subject Performance
                    </h3>
                    <div className="space-y-4">
                        {subjectStats.map((stat, idx) => (
                            <div key={idx} className="border-b border-slate-100 pb-4 last:border-0">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-slate-800">{stat.subject}</span>
                                    <span className="text-2xl font-bold text-indigo-600">
                                        {Math.round(stat.avg_score)}%
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div className="bg-slate-50 p-2 rounded-lg">
                                        <div className="text-slate-500 text-xs">Quizzes</div>
                                        <div className="font-semibold text-slate-800">{stat.total_quizzes}</div>
                                    </div>
                                    <div className="bg-green-50 p-2 rounded-lg">
                                        <div className="text-green-600 text-xs">Best</div>
                                        <div className="font-semibold text-green-700">{stat.best_score}%</div>
                                    </div>
                                    <div className="bg-slate-50 p-2 rounded-lg">
                                        <div className="text-slate-500 text-xs">Accuracy</div>
                                        <div className="font-semibold text-slate-800">
                                            {Math.round((stat.total_correct / stat.total_questions) * 100)}%
                                        </div>
                                    </div>
                                    <div className="bg-indigo-50 p-2 rounded-lg">
                                        <div className="text-indigo-600 text-xs">Questions</div>
                                        <div className="font-semibold text-indigo-700">
                                            {stat.total_correct}/{stat.total_questions}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {improvedTopics.length === 0 && subjectStats.length === 0 && (
                <div className="bg-slate-50 p-8 rounded-3xl shadow-lg border border-slate-200 text-center">
                    <i className="fas fa-chart-line text-5xl text-slate-300 mb-3"></i>
                    <h3 className="text-xl font-bold text-slate-600 mb-2">No Insights Yet</h3>
                    <p className="text-slate-500">Complete a few quizzes to see your progress insights!</p>
                </div>
            )}
        </div>
    );
};

export default InsightsPanel;
